import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import { useContainerWidth } from '../hooks/useContainerWidth';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const PDF_OPTIONS = {
  cMapUrl: 'https://unpkg.com/pdfjs-dist@5.5.207/cmaps/',
  cMapPacked: true,
};

export interface LegalPdfViewerProps {
  url: string;
  downloadFileName?: string;    // passed through for consumer convenience, not used internally
  showMetadata?: boolean;
  showScrollGate?: boolean;
  showDownload?: boolean;       // void stub -- download rendered by consumer in header row
  onPageCountChange?: (numPages: number) => void;
  onLoadError?: () => void;
  onScrolledToBottom?: () => void;
}

export const LegalPdfViewer: React.FC<LegalPdfViewerProps> = ({
  url,
  downloadFileName,
  showMetadata = false,
  showScrollGate = false,
  showDownload = false,
  onPageCountChange,
  onLoadError,
  onScrolledToBottom,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [retryCount, setRetryCount] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(scrollRef);

  // showMetadata reserved for future use
  void showMetadata;
  // showDownload: Download UI rendered by consumer in header row (CONTEXT.md decision)
  void showDownload;
  // downloadFileName: consumer reads from props if needed
  void downloadFileName;

  const onDocumentLoadSuccess = ({ numPages: count }: { numPages: number }) => {
    setNumPages(count);
    setLoadState('loaded');
    onPageCountChange?.(count);
  };

  const onDocumentLoadError = () => {
    setLoadState('error');
    onLoadError?.();
  };

  const handleRetry = () => {
    setLoadState('loading');
    setRetryCount(c => c + 1);
  };

  const handleScroll = useCallback(() => {
    if (!showScrollGate || !scrollRef.current || loadState !== 'loaded') return;
    const { scrollTop, clientHeight, scrollHeight } = scrollRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      onScrolledToBottom?.();
    }
  }, [showScrollGate, loadState, onScrolledToBottom]);

  // Short document edge case: if content fits without scrolling, fire callback immediately on load.
  // Guard scrollHeight > 0 to avoid firing before content renders in DOM (JSDOM returns 0).
  useEffect(() => {
    if (!showScrollGate || loadState !== 'loaded' || !scrollRef.current) return;
    const { scrollHeight, clientHeight } = scrollRef.current;
    if (scrollHeight > 0 && scrollHeight <= clientHeight) {
      onScrolledToBottom?.();
    }
  }, [showScrollGate, loadState, numPages, onScrolledToBottom]);

  return (
    <Box
      ref={scrollRef}
      sx={{ overflow: 'auto', height: '100%', bgcolor: 'grey.100', py: 2 }}
      onScroll={showScrollGate ? handleScroll : undefined}
    >
      {loadState === 'loading' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2">Loading document...</Typography>
        </Box>
      )}

      {loadState === 'error' && (
        <Alert
          severity="error"
          action={
            retryCount < 1 ? (
              <Button size="small" color="inherit" onClick={handleRetry}>
                Retry
              </Button>
            ) : undefined
          }
        >
          Unable to load document. Please try again.
        </Alert>
      )}

      <Document
        key={retryCount}
        file={url}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        options={PDF_OPTIONS}
      >
        {loadState === 'loaded' &&
          Array.from(new Array(numPages), (_, i) => (
            <Box
              key={`page_${i + 1}`}
              sx={{
                bgcolor: 'white',
                boxShadow: 1,
                mx: 'auto',
                mb: 2,
                width: 'fit-content',
                '&:last-child': { mb: 0 },
              }}
            >
              <Page pageNumber={i + 1} width={containerWidth} />
            </Box>
          ))}
      </Document>
    </Box>
  );
};

export default LegalPdfViewer;
